import {
  Greeny,
  Christine,
  Primary,
  SemanticError,
  Warning,
} from '../../../core-components/theme/color-palette/colors';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

export const getInsightTypeChipStyleAndIcon = (type: string) => {
  const lower = type.toLowerCase();

  switch (lower) {
    case 'improvement':
      return {
        icon: WorkspacePremiumOutlinedIcon,
        borderColor: Greeny[300],
        backgroundColor: Greeny[50],
        textColor: Greeny[900],
      };
    case 'accomplishment':
      return {
        icon: WorkspacePremiumOutlinedIcon,
        borderColor: Greeny[300],
        backgroundColor: Greeny[50],
        textColor: Greeny[900],
      };
    case 'incident':
      return {
        icon: CampaignOutlinedIcon,
        borderColor: SemanticError[900],
        backgroundColor: SemanticError[50],
        textColor: SemanticError[900],
      };
    case 'warning':
      return {
        icon: WarningAmberOutlinedIcon,
        borderColor: Warning[600],
        backgroundColor: Warning[50],
        textColor: Warning[900],
      };
    case 'issue':
      return {
        icon: BugReportOutlinedIcon,
        borderColor: Christine[500],
        backgroundColor: Christine[50],
        textColor: Christine[800],
      };
    default:
      return {
        icon: null,
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        textColor: Primary[900],
      };
  }
};

export interface InsightTimeDiff {
  openTime: number;
  closeTime: number;
}

export const formatInsightDateTime = (
  value: string | number | Date | null | undefined
): string => {
  if (value == null || value === '') return '';

  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
};

export const formatInsightTimeDiff = (timeDiff: InsightTimeDiff | null | undefined): string => {
  if (!timeDiff) return '';

  const openTimeMs =
    typeof timeDiff.openTime === 'number' ? timeDiff.openTime : Number(timeDiff.openTime);
  const closeTimeMs =
    typeof timeDiff.closeTime === 'number' ? timeDiff.closeTime : Number(timeDiff.closeTime);

  if (!Number.isFinite(openTimeMs) || openTimeMs <= 0) return '';

  const formatInIst = (value: number): string =>
    new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata',
    }).format(new Date(value));

  const openDate = new Date(openTimeMs);
  const openStr = formatInIst(openTimeMs);

  // Case 1: When Insight is open (closeTime = 0)
  if (!Number.isFinite(closeTimeMs) || closeTimeMs <= 0) return openStr;

  // Case 2: When Insight is closed
  const closeDate = new Date(closeTimeMs);
  const closeStr = formatInIst(closeTimeMs);
  const diffMs = Math.max(0, closeTimeMs - openTimeMs);
  const totalMinutes = diffMs / 60000;
  const totalHours = diffMs / 3600000;
  const totalDays = diffMs / 86400000;

  let durationStr = '';

  if (totalMinutes < 60) {
    const mins = Math.round(totalMinutes);
    durationStr = `${mins} min${mins === 1 ? '' : 's'}`;
  } else if (totalHours < 24) {
    const roundedHours = Math.round(totalHours * 10) / 10;
    durationStr = `${roundedHours} hour${roundedHours === 1 ? '' : 's'}`;
  } else if (totalDays < 7) {
    const days = Math.round(totalDays);
    durationStr = `${days} day${days === 1 ? '' : 's'}`;
  } else if (totalDays < 31) {
    const weeks = Math.round(totalDays / 7);
    durationStr = `${weeks} week${weeks === 1 ? '' : 's'}`;
  } else {
    // Calculate calendar months and remaining days between open and close dates.
    let months =
      (closeDate.getFullYear() - openDate.getFullYear()) * 12 +
      (closeDate.getMonth() - openDate.getMonth());
    const monthAnchor = new Date(openDate);
    monthAnchor.setMonth(monthAnchor.getMonth() + months);
    if (monthAnchor > closeDate) {
      months -= 1;
      monthAnchor.setMonth(monthAnchor.getMonth() - 1);
    }
    const remainingDays = Math.max(
      0,
      Math.floor((closeDate.getTime() - monthAnchor.getTime()) / 86400000)
    );

    durationStr = `${months} month${months === 1 ? '' : 's'}`;
    if (remainingDays > 0) {
      durationStr += ` ${remainingDays} day${remainingDays === 1 ? '' : 's'}`;
    }
  }

  return `${openStr} - ${closeStr} (${durationStr})`;
};

export interface DateRange {
  fromDate: string;
  toDate: string;
}
